import { useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, UnternehmenStammdaten } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand }
  from "@kundeninfo/ui";
import { Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { landName } from "../../format.js";
import { Meldung } from "./gemeinsam.js";
import "./fussbloecke.css";

/**
 * DER ABSCHNITT „FUSSBLÖCKE FÜR BELEGE" in Verwaltung › Unternehmen (Belegdesign Fußbereich, Entscheid 246; Spec
 * docs/superpowers/specs/2026-10-20-belegdesign-fussbereich-design.md). Drei freie Zeilenblöcke — Fuß links, Fuß
 * Mitte, Fuß rechts — mit je höchstens acht Zeilen; der Belegdesigner setzt sie über {fusslinks}, {fussmitte} und
 * {fussrechts} in den Fuß, im Render-Modell stehen sie als `absender.fussbloecke`.
 *
 * Bauart wie der Block „DATEV": Das Detail LIEST, „Ändern" öffnet ein striktes Modalpanel mit eigenem Schreibweg
 * (`PUT …/fussbloecke`), eigenen Feldfehlern und eigener Quittung. Die Zeilen pflegt man wie die Anschriftzeilen
 * einer Adresse: ein Feld je Zeile, Entfernen je Zeile, „Zeile hinzufügen" darunter. Vor dem Senden wird wie auf dem
 * Server getrimmt und Leeres entfernt — so hängt ein Befund `fussbloecke[i][j]` an genau der Zeile, die ihn
 * verursacht hat. „Aus Stammdaten vorschlagen" (Frage 261) füllt die drei Blöcke aus Anschrift, Kontakt und
 * Bankverbindung; steht schon etwas, fragt es vorher.
 *
 * Eigener CSS-Block `fb` (fussbloecke.css), Farben nur aus den ME-Tokens.
 */

export type FussbloeckeClient = Pick<ApiClient, "setCompanyFooterBlocks">;

export type FussbloeckeBlockProps = {
  client: FussbloeckeClient;
  unternehmen: UnternehmenStammdaten;
  /** Ohne ADMIN kein „Ändern" (der Server weist ohnehin mit 403 ab). */
  nurLesen: boolean;
  /** Sperrt „Ändern", solange das Stammdaten-Panel darüber liegt (Muster `verdeckt` der Adress-Karte). */
  verdeckt: boolean;
  /** Nach dem Speichern — der Bereich holt seine Liste neu. */
  onGespeichert: (neu: UnternehmenStammdaten) => void;
};

/** Die Namen der Blöcke — dieselben wie in der Domäne (`FUSSBLOCK_NAMEN`; web darf domain nicht importieren). */
export const FUSSBLOCK_NAMEN = ["Fuß links", "Fuß Mitte", "Fuß rechts"] as const;
/** Grenzen wie in der Domäne (`FUSSBLOCK_ZEILEN_MAX`, `FUSSBLOCK_ZEICHEN_MAX`); verbindlich prüft der Server. */
export const ZEILEN_MAX = 8;
export const ZEICHEN_MAX = 120;

/** Trimmt jede Zeile und lässt leere weg — dieselbe Normalisierung wie der Server (`normalizeFussbloecke`). */
export function normalisiert(bloecke: readonly (readonly string[])[]): string[][] {
  return bloecke.map((b) => b.map((z) => z.trim()).filter((z) => z !== ""));
}

/** Der Vorschlag aus den Stammdaten (Frage 261): links Name und Anschrift (Ausland mit Landname), Mitte Telefon,
 *  E-Mail und USt-IdNr., rechts IBAN in Vierergruppen und BIC — nur Gepflegtes, je Zeile höchstens 120 Zeichen. */
export function fussVorschlag(u: UnternehmenStammdaten): string[][] {
  const gepflegt = (zeilen: (string | null)[]): string[] => zeilen
    .filter((z): z is string => z !== null && z.trim() !== "")
    .map((z) => z.trim().slice(0, ZEICHEN_MAX));
  const ort = [u.postalCode, u.city].filter((x) => x !== null && x !== "").join(" ");
  return [
    gepflegt([u.name, u.street, ort === "" ? null : ort, u.country !== "DE" ? landName(u.country) : null]),
    gepflegt([u.phone === null ? null : `Telefon ${u.phone}`, u.email === null ? null : `E-Mail ${u.email}`,
              u.vatId === null ? null : `USt-IdNr. ${u.vatId}`]),
    gepflegt([u.iban === null ? null : `IBAN ${u.iban.replace(/(.{4})(?=.)/g, "$1 ")}`,
              u.bic === null ? null : `BIC ${u.bic}`]),
  ];
}

export function FussbloeckeBlock(p: FussbloeckeBlockProps): ReactElement {
  const u = p.unternehmen;
  const [entwurf, setEntwurf] = useState<string[][] | null>(null);
  const [erfolg, setErfolg] = useState(false);
  const { bestaetigen } = useBestaetigung();

  const formular = useFormularZustand<UnternehmenStammdaten | null>({
    speichern: async () => {
      if (entwurf === null) return null;
      // Wie der Server normalisieren UND die Maske nachziehen: Ein Befund `fussbloecke[i][j]` meint die j-te nicht
      // leere Zeile (Review Focus 5).
      const gesendet = normalisiert(entwurf);
      setEntwurf(gesendet);
      return await p.client.setCompanyFooterBlocks(u.id, gesendet);
    },
    neuLaden: async () => { setEntwurf(u.fussbloecke.map((b) => [...b])); },
    bekannteFelder: (feld) => /^fussbloecke(\[\d+\](\[\d+\])?)?$/.test(feld),
    beschriftungen: { fussbloecke: "Fußblöcke" },
  });
  const feldFehler = formular.feldFehler;

  const geaendert = entwurf !== null
    && JSON.stringify(normalisiert(entwurf)) !== JSON.stringify(u.fussbloecke);
  useBeforeUnload(geaendert);

  function oeffnen(): void {
    formular.zuruecksetzen();
    setErfolg(false);
    setEntwurf(u.fussbloecke.map((b) => [...b]));
  }

  async function abbrechen(): Promise<void> {
    if (formular.laeuft) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setEntwurf(null);
    formular.zuruecksetzen();
  }

  async function speichern(): Promise<void> {
    const neu = await formular.aktion();
    if (neu === undefined || neu === null) return;
    setEntwurf(null);
    setErfolg(true);
    p.onGespeichert(neu);
  }

  function aendern(i: number, neu: (block: string[]) => string[]): void {
    setEntwurf((e) => (e === null ? e : e.map((b, k) => (k === i ? neu(b) : b))));
  }

  async function vorschlagen(): Promise<void> {
    if (entwurf === null) return;
    const vorschlag = fussVorschlag(u);
    const stehtEtwas = entwurf.some((b) => b.some((z) => z.trim() !== ""));
    if (stehtEtwas && !(await bestaetigen({
      titel: "Fußblöcke ersetzen?",
      // Kleine gestapelte Frage über dem Panel (me-design bestaetigen(), Hausregel 19.10.) — ein Satz, reiner Text.
      text: "Der Vorschlag aus den Stammdaten ersetzt die Zeilen aller drei Blöcke; gespeichert wird erst mit "
        + "„Speichern“.",
      bestaetigenText: "Ersetzen",
    }))) return;
    setEntwurf(vorschlag);
  }

  const leer = u.fussbloecke.every((b) => b.length === 0);

  return (
    <section className="fb" aria-label="Fußblöcke für Belege">
      <h3 className="fb__untertitel">Fußblöcke für Belege</h3>
      <p className="fb__hinweis">
        Drei freie Blöcke für den Fuß der ersten Belegseite — im Belegdesigner über {"{fusslinks}"},
        {" {fussmitte}"} und {"{fussrechts}"}; je Block höchstens {ZEILEN_MAX} Zeilen.
      </p>
      {erfolg && entwurf === null && <Hinweis art="ok">Gespeichert.</Hinweis>}
      {/* Nebeneinander erst ab 720 px BEHÄLTERBREITE (Container-Query in fussbloecke.css), sonst untereinander
          links → Mitte → rechts (Frage 260) — die Detailspalte ist auch am breiten Bildschirm oft nur ~500 px breit,
          drei Spalten brächen dort mitten im Wort um (Fixrunde 1). */}
      <div className="fb__spalten">
        {FUSSBLOCK_NAMEN.map((name, i) => {
          const zeilen = u.fussbloecke[i] ?? [];
          return (
            <div key={name} className="fb__spalte">
              <span className="fb__name">{name}</span>
              {zeilen.length === 0
                ? <span className="fb__leer">nicht gepflegt</span>
                : zeilen.map((z, j) => <span key={j} className="fb__zeilentext">{z}</span>)}
            </div>
          );
        })}
      </div>
      {leer && (
        <p className="fb__hinweis">
          Ohne Fußblöcke bleiben {"{fusslinks}"}, {"{fussmitte}"} und {"{fussrechts}"} auf dem Beleg leer.
        </p>
      )}
      {!p.nurLesen && (
        <div className="fb__aktionen">
          <KiKnopf deaktiviert={entwurf !== null || p.verdeckt} name={`Fußblöcke von ${u.name} ändern`}
            onClick={oeffnen}>
            Ändern
          </KiKnopf>
        </div>
      )}

      {entwurf !== null && (
        <Modal titel={`Fußblöcke von ${u.name}`} breite="breit" onAbbrechen={abbrechen}
          abbrechenGesperrt={formular.laeuft}>
          {formular.meldung !== null && <Meldung text={formular.meldung} />}
          {/* `breit` (Entscheid 135 B): im Modal „breit“ füllt das Formular das Blatt. */}
          <Formular beschriftung="oben" breit {...formular.formularProps}>
            <div className="fb__werkzeuge">
              <KiKnopf deaktiviert={formular.laeuft} onClick={() => { void vorschlagen(); }}>
                Aus Stammdaten vorschlagen
              </KiKnopf>
            </div>
            {feldFehler.fussbloecke !== undefined && <Hinweis art="fehler">{feldFehler.fussbloecke}</Hinweis>}
            {entwurf.map((block, i) => {
              const name = FUSSBLOCK_NAMEN[i] ?? `Block ${i + 1}`;
              const blockFehler = feldFehler[`fussbloecke[${i}]`];
              return (
                <section key={name} className="fb__block" aria-label={name}>
                  <h4 className="fb__blocktitel">{name}</h4>
                  {block.map((zeile, j) => {
                    const fehler = feldFehler[`fussbloecke[${i}][${j}]`];
                    return (
                      <div key={j} className="fb__zeile">
                        <Eingabe id={`fb-${u.id}-${i}-${j}`} name={`fussbloecke[${i}][${j}]`}
                          className="fb__eingabe" aria-label={`${name}, Zeile ${j + 1}`} wert={zeile}
                          maxLength={ZEICHEN_MAX}
                          {...(fehler === undefined ? {} : { fehler })}
                          {...(formular.laeuft ? { gesperrt: true } : {})}
                          onGeaendert={(d) => aendern(i, (b) => b.map((z, k) => (k === j ? d.wert : z)))} />
                        <LoeschKnopf name={`${name}: Zeile ${j + 1} entfernen`} deaktiviert={formular.laeuft}
                          onClick={() => aendern(i, (b) => b.filter((_, k) => k !== j))} />
                      </div>
                    );
                  })}
                  <div className="fb__fuss">
                    <KiKnopf deaktiviert={formular.laeuft || block.length >= ZEILEN_MAX}
                      name={`${name}: Zeile hinzufügen`} onClick={() => aendern(i, (b) => [...b, ""])}>
                      Zeile hinzufügen
                    </KiKnopf>
                    {block.length >= ZEILEN_MAX
                      && <span className="fb__hinweis">Höchstens {ZEILEN_MAX} Zeilen.</span>}
                  </div>
                  {blockFehler !== undefined && <Hinweis art="fehler">{blockFehler}</Hinweis>}
                </section>
              );
            })}
          </Formular>
          <ModalFuss speichernText="Speichern" speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={formular.laeuft}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
